import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const LOGO_URL = "/logo.png";

const Header = () => {
  const { t, lang, setLang, languages } = useLang();
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
    setLangOpen(false);
  }, [location.pathname]);

  const navItems = [
    { to: "/about", label: t.nav.about },
    { to: "/services", label: t.nav.services },
    { to: "/airport-transfers", label: t.nav.airport },
    { to: "/fleet", label: t.nav.fleet },
    { to: "/pricing", label: t.nav.pricing },
    { to: "/contact", label: t.nav.contact },
  ];

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-zinc-200 shadow-sm"
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 lg:px-12 flex items-center justify-between h-24 md:h-28">
        <Link to="/" data-testid="header-logo-link" className="flex items-center gap-2.5 group">
          <img src={LOGO_URL} alt="Brothers Taxi" className="h-28 w-28 md:h-32 md:w-32 object-contain" />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `px-3 py-2 text-base font-medium rounded-full transition-colors ${
                  isActive ? "bg-zinc-900 text-white" : "text-zinc-700 hover:text-zinc-900 hover:bg-zinc-100"
                }`
              }
              end={item.to === "/"}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href="tel:+355682222286"
            data-testid="header-call-link"
            className="hidden xl:flex items-center gap-2.5 pl-1.5 pr-4 py-1.5 rounded-full bg-zinc-50 hover:bg-zinc-900 hover:text-white text-zinc-900 border border-zinc-200 transition-all group"
          >
            <span className="h-8 w-8 rounded-full bg-[#FFD500] flex items-center justify-center flex-shrink-0 group-hover:rotate-[-8deg] transition-transform">
              <Phone className="h-3.5 w-3.5 text-zinc-900" strokeWidth={2.5} />
            </span>
            <span className="flex flex-col leading-tight text-left">
              <span className="text-[9px] uppercase tracking-[0.15em] font-semibold text-zinc-500 group-hover:text-zinc-400">24/7</span>
              <span className="text-sm font-semibold tracking-tight">+355 68 22 22 286</span>
            </span>
          </a>

          <div className="relative">
            <button
              data-testid="lang-switcher-button"
              onClick={() => setLangOpen((v) => !v)}
              className="flex items-center gap-1 px-3 py-2 text-xs uppercase tracking-widest font-extrabold text-zinc-900 hover:bg-zinc-100 rounded-full transition-colors"
            >
              {lang}
              <ChevronDown className="h-3 w-3" />
            </button>
            {langOpen && (
              <div
                data-testid="lang-dropdown"
                className="absolute right-0 top-full mt-2 bg-white border border-zinc-200 min-w-[160px] py-2 z-50 rounded-2xl shadow-taxi-lift overflow-hidden"
              >
                {languages.map((l) => (
                  <button
                    key={l.code}
                    data-testid={`lang-option-${l.code}`}
                    onClick={() => {
                      setLang(l.code);
                      setLangOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2.5 text-sm font-semibold hover:bg-[#FFD500] transition-colors ${
                      lang === l.code ? "bg-zinc-900 text-[#FFD500]" : "text-zinc-900"
                    }`}
                  >
                    {l.label} — {l.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/book"
            data-testid="header-book-button"
            className="hidden md:inline-flex bt-btn-primary text-xs px-5 py-3"
          >
            {t.common.bookTaxi}
          </Link>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 text-zinc-900"
            aria-label="Menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-white border-t border-zinc-200 max-h-[calc(100vh-5rem)] overflow-y-auto"
        >
          <nav className="flex flex-col px-6 py-6 gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`mobile-nav-${item.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `py-3 px-3 text-base font-bold uppercase tracking-wide border-b border-zinc-100 rounded-lg ${
                    isActive ? "text-zinc-900 bg-[#FFD500]" : "text-zinc-700"
                  }`
                }
                end={item.to === "/"}
              >
                {item.label}
              </NavLink>
            ))}
            <Link to="/book" data-testid="mobile-book-button" className="bt-btn-primary mt-4 w-full">
              {t.common.bookTaxi}
            </Link>
            <a href="tel:+355682222286" data-testid="mobile-call-link" className="bt-btn-outline mt-2 w-full">
              <Phone className="h-4 w-4" /> +355 68 22 22 286
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
